// Start loading the map while the splash screen is still up.
//
// The splash (ServerConnectGate) stays at least a few seconds, and the map used
// to start fetching its pictures only after it closed. Now the moment the
// player is known to be heading for the map, every picture the map draws first
// is requested in the background — islands first, then houses, owners' avatars
// and the sale listings — through the same PIXI.Assets keys the map uses, so
// the map later finds them decoded in the cache. Nothing here is awaited by the
// splash: it never delays the connection.
//
// The house the player last visited is preloaded too — its pictures decoded and
// its furniture list in the query cache — so walking in is instant.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { getListingsMarketplaceListingsGet } from '../../client';
import { getMapObjects, type MapObject } from '../../models/mapObject';
import { getFloorTexturePath, getFurnitureImagePath, getWallTexturePath } from '../../data/furniture';
import { DECOR_ATLAS_JSON_PATH, getDecorImagePath, isDecorInteractiveType } from '../../data/mapDecorAssets';
import { fetchHouseFurniture } from '../room/state/layoutSync';
import { PLOT_MARKER_TEXTURE_PATH } from './mapScene';

/** Island type → its picture (/game-assets/islands/<n>.webp). */
export const ISLAND_TYPE_TO_IMAGE: Record<string, number> = {
  island_paradise: 0,
  island_coral: 1,
  island_lagoon: 2,
  island_opal: 3,
  island_jade: 4,
};

export const CLOUDS_OVERLAY_PATH = '/game-assets/overlays/clouds.webp';

export const islandImagePath = (type: string): string => `/game-assets/islands/${ISLAND_TYPE_TO_IMAGE[type] ?? 0}.webp`;

export const houseImagePath = (realEstateId: number | string): string => `/game-assets/map-objects/real-estate/${realEstateId}.webp`;

/** The PIXI.Assets source of an owner's avatar on the map (the smallest Telegram size, via our proxy). */
export function mapAvatarSrc(photoUrl: string): string {
  const origin = window.location.origin;
  const proxyBase = origin.includes('ngrok-free.app') || origin.includes('localhost')
    ? `${origin}/api` : 'https://notapi.web3islands.com';
  const smallUrl = photoUrl.replace('/userpic/320/', '/userpic/160/');
  return `${proxyBase}/proxy/avatar?url=${encodeURIComponent(smallUrl)}`;
}

/** Load an avatar texture the way the map draws it (one shared cache entry per photo). */
export function loadMapAvatar(photoUrl: string): Promise<PIXI.Texture | null> {
  return PIXI.Assets.load<PIXI.Texture>({ src: mapAvatarSrc(photoUrl), parser: 'loadTextures' })
    .then((tex) => {
      if (tex.source) {
        tex.source.autoGenerateMipmaps = true;
        tex.source.scaleMode = 'linear';
      }
      return tex;
    })
    .catch(() => null);
}

const load = (src: string): Promise<unknown> => PIXI.Assets.load(src).catch(() => null);

/** Every texture path the map draws for these objects (what useMapObjectsRender loads). */
export function mapTexturePaths(objects: readonly MapObject[]): string[] {
  const paths = new Set<string>();
  for (const o of objects) {
    if (o.type.startsWith('island_')) paths.add(islandImagePath(o.type));
    else if (o.type === 'house' && o.data?.real_estate_id != null) paths.add(houseImagePath(o.data.real_estate_id));
    else if (o.type === 'cityhall') paths.add('/game-assets/map-objects/cityhall.webp');
    else if (o.type === 'shop') paths.add('/game-assets/map-objects/shop.webp');
    else if (o.type === 'hell_of_flame') paths.add('/game-assets/map-objects/hall-of-fame.webp');
    else if (o.type.startsWith('decor_') && isDecorInteractiveType(o.type)) paths.add(getDecorImagePath(o.type));
  }
  return [...paths];
}

let mapPreloadStarted = false;

/**
 * Begin loading the map's pictures and data in the background. Safe to call
 * more than once; never throws, never awaited by the caller.
 */
export function preloadMap(queryClient: QueryClient): void {
  if (mapPreloadStarted) return;
  mapPreloadStarted = true;

  // What every map draws, known without any data: the islands first — they are the map.
  for (const type of Object.keys(ISLAND_TYPE_TO_IMAGE)) void load(islandImagePath(type));
  void load(PLOT_MARKER_TEXTURE_PATH);
  void load(DECOR_ATLAS_JSON_PATH);
  void load(CLOUDS_OVERLAY_PATH);

  void queryClient.prefetchQuery({
    queryKey: ['marketplaceListings'],
    queryFn: async () => {
      const { data } = await getListingsMarketplaceListingsGet({ throwOnError: true });
      return data;
    },
    staleTime: 60_000,
  }).catch(() => {});

  void queryClient.fetchQuery({ queryKey: ['mapObjects'], queryFn: getMapObjects })
    .then((objects) => {
      for (const path of mapTexturePaths(objects)) void load(path);
      const photos = new Set<string>();
      for (const o of objects) if (o.type === 'house' && o.owner?.photo_url) photos.add(o.owner.photo_url);
      for (const photo of photos) void loadMapAvatar(photo);
    })
    .catch(() => { /* the map loads them itself */ });
}

// ── The house the player last visited ──

const LAST_HOUSE_KEY = 'lastVisitedHouse';

/** Remember the real house the player is in (not the tutorial's practice room). */
export function rememberLastHouse(mapObjectId: number): void {
  if (mapObjectId <= 0) return;
  try { localStorage.setItem(LAST_HOUSE_KEY, String(mapObjectId)); } catch { /* private mode */ }
}

export function lastVisitedHouse(): number | null {
  try {
    const id = Number(localStorage.getItem(LAST_HOUSE_KEY));
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

let lastHousePreloadStarted = false;

/** Fetch the last visited house's furniture list and decode its pictures. */
export function preloadLastHouse(queryClient: QueryClient): void {
  const id = lastVisitedHouse();
  if (id === null || lastHousePreloadStarted) return;
  lastHousePreloadStarted = true;
  void queryClient.fetchQuery({ queryKey: ['houseFurniture', id], queryFn: () => fetchHouseFurniture(id) })
    .then((items) => {
      const urls = new Set<string>();
      let wall = 55;
      let floor = 56;
      for (const f of items) {
        const catalog = f.furniture_catalog;
        if (!catalog || !f.is_placed || f.room_index !== 0) continue;
        if (catalog.category === 'walls') wall = catalog.id;
        else if (catalog.category === 'floors') floor = catalog.id;
        else urls.add(getFurnitureImagePath(catalog));
      }
      urls.add(getWallTexturePath(wall));
      urls.add(getFloorTexturePath(floor));
      // Decoded into the same PIXI cache entries the room uses — it opens whole, at
      // once (room art is 2x since build-room-art: ~36 MB for a full room).
      for (const url of urls) void load(url);
    })
    .catch(() => { /* entering the house loads it as before */ });
}
